/**
 * The Mini App's credential (DECISION-32).
 *
 * ONE exchange, then a bearer token. `initData` goes to `/auth/miniapp` once
 * and comes back as the same signed session token the cookie carries; every
 * request after that sends `Authorization: Bearer <token>`.
 *
 * Why not just the cookie: on Telegram Web the Mini App is rendered in an
 * iframe, which is a cross-site context, and the app's `SameSite=Lax` cookie
 * is not sent there at all. The app would authenticate and then 401 on its
 * very next request. Weakening the cookie to `SameSite=None` was the other
 * option and it would trade the WEB app's CSRF posture away to fix a surface
 * that has a better answer available.
 *
 * Why not send `initData` on every request, which much of the ecosystem does:
 * it stays valid for `TELEGRAM_AUTH_MAX_AGE_SECONDS` (86400 by default), so
 * anyone who sees one request can replay it for a day — and it would put a
 * Telegram-signed identity blob into every access-log line.
 *
 * IN MEMORY, never `localStorage`. An httpOnly cookie beats a JS-readable
 * token against XSS and `localStorage` is strictly worse than both. Memory
 * costs nothing here because re-authentication is silent: the launch
 * `initData` is in `sessionStorage`, so a 401 re-exchanges and retries without
 * the member seeing anything. That also covers a reload, where the in-memory
 * token is gone by definition.
 */

import type { MiniAppAuthResponse } from '@vlastiq/contracts';
import { ApiError, toApiError } from '../auth/error.ts';
import { getInitData, isMiniApp } from './launch-params.ts';

let token: string | null = null;

/** In-flight exchange, so a burst of parallel 401s produces ONE re-exchange. */
let inFlight: Promise<string | null> | null = null;

export function getAuthToken(): string | null {
  return token;
}

/** Drop the token. The server session is stateless, so this is the whole of it. */
export function clearAuthToken(): void {
  token = null;
}

/**
 * A FAILED exchange throws with the server's own code, and that matters more
 * than it looks.
 *
 * The three ways `/auth/miniapp` says no are the three ways every other
 * endpoint says no, and each needs a different screen:
 *
 *   403 not_a_member          -> the not-a-member page
 *   503 telegram_unavailable  -> "cannot verify right now", with a retry
 *   401 invalid_telegram_payload -> "reopen the app from Telegram"
 *
 * Swallowing these and letting the subsequent request 401 instead would send a
 * non-member to a login page they cannot complete inside a webview — the
 * OIDC flow needs a top-level browser redirect. A member who genuinely left
 * the group would see a login loop rather than an answer.
 *
 * `invalid_telegram_payload` is the one with no equivalent on the website, and
 * it is NOT a membership problem. Telegram's SDK restores launch parameters
 * from `sessionStorage` on a reload carrying the ORIGINAL `auth_date`, which
 * ages; past `TELEGRAM_AUTH_MAX_AGE_SECONDS` the server correctly refuses it.
 * A retry cannot help — the launch data itself is what was rejected.
 */
async function exchange(): Promise<string | null> {
  const initData = getInitData();
  if (initData === null) return null;

  const response = await fetch('/auth/miniapp', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ initData }),
  });

  if (!response.ok) {
    token = null;
    throw await toApiError(response);
  }

  const body = (await response.json()) as MiniAppAuthResponse;
  token = typeof body.token === 'string' && body.token !== '' ? body.token : null;
  if (token === null) {
    // A 200 with no usable token is a server contract violation, not an auth
    // denial. Saying so beats an unexplained 401 on the next request.
    throw new ApiError(500, 'internal', 'Сервер не вернул токен сессии.');
  }
  return token;
}

/**
 * Obtain a token, exchanging if necessary. Returns null outside a Mini App.
 *
 * Concurrent callers share one exchange: the app fires several requests on
 * first render, and without this a cold start would POST `initData` once per
 * in-flight request.
 */
export function ensureAuthToken(force = false): Promise<string | null> {
  if (!isMiniApp()) return Promise.resolve(null);
  if (token !== null && !force) return Promise.resolve(token);

  inFlight ??= exchange().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
